import type { Basis, ScenarioInput } from '@exigo/shared';
import { Card } from '../../components/ui/Card';
import { NumberInput, TextInput } from '../../components/ui/TextInput';
import { Select } from '../../components/ui/Select';
import { dollarsToCents } from '../../lib/money';
import calcStyles from './CalculatorPage.module.css';

interface SeatsAcvCardProps {
  input: ScenarioInput;
  setInput: (updater: (prev: ScenarioInput) => ScenarioInput) => void;
  basesInArea: Basis[];
}

/** Seat count and Dynamics 365 ACV — shown only for the areas that size on them. */
export function SeatsAcvCard({ input, setInput, basesInArea }: SeatsAcvCardProps): React.JSX.Element | null {
  const showSeats = basesInArea.includes('seats');
  const showAcv = basesInArea.includes('acv');
  if (!showSeats && !showAcv) return null;

  return (
    <Card className={calcStyles.section}>
      <p className={calcStyles.sectionTitle}>Seats and contract value</p>
      <div className={calcStyles.grid}>
        {showSeats && (
          <>
            <div className={calcStyles.field}>
              <label className={calcStyles.fieldLabel} htmlFor="seats">
                Seats
              </label>
              <NumberInput
                id="seats"
                value={input.seats}
                onValueChange={(v) => { setInput((p) => ({ ...p, seats: Math.round(v) })); }}
                min={0}
                ariaLabel="Seats"
              />
              <span className={calcStyles.fieldHint}>
                Check each engagement&rsquo;s seat definition — some count purchased totals, others
                count incremental net paid adds only.
              </span>
            </div>
            <div className={calcStyles.field}>
              <label className={calcStyles.fieldLabel} htmlFor="seatPrice">
                Sell price per seat, per month
              </label>
              <TextInput
                id="seatPrice"
                type="number"
                min={0}
                step={0.01}
                placeholder="e.g. 45"
                value={input.seatPrice === null ? '' : input.seatPrice / 100}
                onChange={(e) => {
                  const raw = e.target.value;
                  setInput((p) => ({
                    ...p,
                    seatPrice: raw === '' ? null : dollarsToCents(Number(raw)),
                  }));
                }}
              />
              <span className={calcStyles.fieldHint}>
                Your actual sell rate, not list. Blank leaves contract value unknown — the guide
                contains no pricing, so nothing here is assumed.
              </span>
            </div>
            <div className={calcStyles.field}>
              <span className={calcStyles.fieldLabel}>Seat price currency</span>
              <Select
                value={input.seatPriceCurrency}
                onChange={(v) => {
                  setInput((p) => ({ ...p, seatPriceCurrency: v as ScenarioInput['seatPriceCurrency'] }));
                }}
                options={[
                  { value: 'AUD', label: 'AUD' },
                  { value: 'USD', label: 'USD' },
                ]}
                ariaLabel="Seat price currency"
              />
            </div>
          </>
        )}

        {showAcv && (
          <div className={calcStyles.field}>
            <label className={calcStyles.fieldLabel} htmlFor="acv">
              Dynamics 365 incremental ACV (USD)
            </label>
            <NumberInput
              id="acv"
              value={input.acv / 100}
              onValueChange={(v) => { setInput((p) => ({ ...p, acv: dollarsToCents(v) })); }}
              min={0}
              ariaLabel="Dynamics 365 incremental ACV"
            />
            <span className={calcStyles.fieldHint}>
              Annual contract value of eligible D365 products added in the same month.
            </span>
          </div>
        )}
      </div>
    </Card>
  );
}
